/*
 * Kör en import batchvis. Två lägen:
 *  - mock:  simulerar (exempeldata) med liten fördröjning per batch.
 *  - live:  anropar gym-API:ts bulk-mutationer (importGymSessions,
 *           importBodyMeasurements, importActivities, importDailySteps) – en
 *           batch = ett anrop. Nya övningstyper skapas före passen och
 *           övningsmappningarna sparas som alias efteråt.
 *
 * Flera års data ⇒ tusentals poster: därför batchar, progress per batch och
 * avbryt mellan batchar (shouldCancel()). Dedup via externalId gör att en
 * avbruten import kan köras om.
 *
 * onProgress({ datasetKey, done, total, label }) anropas per batch.
 * Returnerar { datasets: { key: { total, done, failed, skipped, reason } }, errors: [], cancelled }.
 */
import { DATASET_DEFS, datasetPlannedCount } from './importModel.js';

const sleep = ms => new Promise(r => setTimeout(r, ms));

// Poster per anrop. Pass är tunga (övningar+set), övriga lätta.
export const BATCH_SIZE = { sessions: 25, activities: 200, weights: 200, measurements: 200, steps: 365, height: 1 };
const MAX_ERRORS = 500;

export function buildPlan(bundle) {
  return DATASET_DEFS
    .map(def => ({ key: def.key, label: def.label, icon: def.icon, unit: def.unit, ds: bundle.datasets[def.key] }))
    .filter(p => datasetPlannedCount(p.ds) > 0)
    .map(p => ({ ...p, items: p.ds.items.filter(i => i.selected) }));
}

export async function runImport(bundle, { mode = 'mock', api = null, token = null, onProgress = () => {}, shouldCancel = () => false, ctx = {} } = {}) {
  const plan = buildPlan(bundle);
  const result = { mode, startedAt: new Date().toISOString(), finishedAt: null, datasets: {}, errors: [], cancelled: false };

  if (mode === 'live' && plan.some(p => p.key === 'sessions')) {
    result.errors.push(...await prepareSessions(bundle, { api, token, onProgress }));
  }

  outer:
  for (const p of plan) {
    const r = { total: p.items.length, done: 0, failed: 0, skipped: 0, serverSkipped: 0, reason: null };
    result.datasets[p.key] = r;
    const handler = mode === 'mock' ? mockHandler : LIVE_HANDLERS[p.key];

    if (!handler) {
      r.skipped = r.total;
      r.reason = 'Kräver backend-steget (steg 2)';
      onProgress({ datasetKey: p.key, done: 0, total: r.total, label: r.reason });
      continue;
    }

    const size = BATCH_SIZE[p.key] || 100;
    for (let i = 0; i < p.items.length; i += size) {
      if (shouldCancel()) {
        result.cancelled = true;
        r.skipped = r.total - r.done - r.failed;
        r.reason = 'Avbruten';
        break outer;
      }
      const batch = p.items.slice(i, i + size);
      onProgress({ datasetKey: p.key, done: i, total: r.total, label: `${itemLabel(p.key, batch[0])} … (batch ${Math.floor(i / size) + 1}/${Math.ceil(p.items.length / size)})` });
      const tok = mode === 'live' ? (typeof token === 'function' ? await token() : token) : null;
      try {
        const out = await handler(batch, { bundle, api, token: tok, ctx, datasetKey: p.key });
        r.done += out.done;
        r.failed += out.failed.length;
        r.serverSkipped += out.serverSkipped || 0;
        for (const f of out.failed) {
          if (result.errors.length < MAX_ERRORS) result.errors.push({ datasetKey: p.key, label: itemLabel(p.key, f.item), message: f.message });
        }
      } catch (e) {
        // Hela batchen föll (nätverk, auth …) – räkna alla som fel och gå vidare.
        r.failed += batch.length;
        if (result.errors.length < MAX_ERRORS) result.errors.push({ datasetKey: p.key, label: `${p.label}, batch ${Math.floor(i / size) + 1}`, message: e?.message || String(e) });
      }
    }
    onProgress({ datasetKey: p.key, done: r.done + r.failed, total: r.total, label: '' });
    // Markera oavslutade som hoppade när loopen bröts av avbryt.
    if (result.cancelled) r.skipped = r.total - r.done - r.failed;
  }
  // Datatyper som aldrig påbörjades vid avbryt.
  if (result.cancelled) {
    for (const p of plan) {
      if (!result.datasets[p.key]) result.datasets[p.key] = { total: p.items.length, done: 0, failed: 0, skipped: p.items.length, reason: 'Avbruten' };
    }
  }

  if (mode === 'live' && plan.some(p => p.key === 'sessions')) {
    try { await saveAliases(bundle, { api, token }); }
    catch (e) { result.errors.push({ datasetKey: 'sessions', label: 'Spara övningsmappningar', message: e?.message || String(e) }); }
  }

  result.finishedAt = new Date().toISOString();
  return result;
}

function itemLabel(key, item) {
  if (!item) return '';
  if (item.label && !item.startedAt && !item.date) return item.label;
  const d = (item.startedAt || item.date || '').slice(0, 10);
  switch (key) {
    case 'sessions': return `${d} · ${item.name}`;
    case 'activities': return `${d} · ${item.name || item.googleType}`;
    case 'weights': return `${d} · ${item.weightKg} kg`;
    case 'steps': return `${d} · ${item.steps} steg`;
    case 'height': return `${item.heightCm} cm`;
    default: return d;
  }
}

async function mockHandler(batch, { datasetKey }) {
  await sleep(datasetKey === 'sessions' ? 120 : 60);
  return { done: batch.filter(i => !i.mockFail).length, failed: batch.filter(i => i.mockFail).map(item => ({ item, message: 'Simulerat fel' })), serverSkipped: 0 };
}

// ── Live: bulk-mutationer i gym-API:t (en batch = ett anrop) ─────────────

function toRunnerResult(r, batch, labelOf) {
  const failed = (r?.failed || []).map(f => ({ item: batch.find(i => i.externalId && i.externalId === f.externalId) || { label: f.label }, message: f.message }));
  const ok = (r?.created || 0) + (r?.updated || 0) + (r?.skipped || 0);
  return { done: ok, failed, serverSkipped: r?.skipped || 0, created: r?.created || 0, updated: r?.updated || 0, labelOf };
}

const LIVE_HANDLERS = {
  async sessions(batch, { api, token, bundle }) {
    const typeMap = bundle.datasets.sessions.typeMap || new Map();
    const input = [];
    const dropped = [];
    for (const s of batch) {
      const exercises = s.exercises
        .filter(e => typeMap.has(e.csvName))
        .map((e, i) => ({
          exerciseTypeId: typeMap.get(e.csvName), order: i + 1, notes: null,
          sets: e.sets.map((st, j) => ({ order: j + 1, reps: st.reps ?? null, weight: st.weight ?? null, distanceMeters: st.distanceMeters ?? null, durationSeconds: st.durationSeconds ?? null, notes: st.notes ?? null })),
        }));
      if (!exercises.length) { dropped.push({ item: s, message: 'Alla övningar i passet ignorerades' }); continue; }
      input.push({ externalId: s.externalId, startedAt: s.startedAt, name: s.name, durationSeconds: s.endedAt ? Math.round((new Date(s.endedAt) - new Date(s.startedAt)) / 1000) : null, notes: s.notes ?? null, exercises });
    }
    const r = input.length ? (await api.importGymSessions(input, token)).importGymSessions : { created: 0, skipped: 0, failed: [] };
    const out = toRunnerResult(r, batch);
    out.failed.push(...dropped);
    return out;
  },

  async activities(batch, { api, token, bundle }) {
    const typeFor = new Map(bundle.datasets.activities.typeMappings.map(t => [t.googleType, t]));
    const input = [];
    const dropped = [];
    for (const a of batch) {
      const tm = typeFor.get(a.googleType);
      if (!tm || tm.action !== 'map' || !tm.activityTypeId) { dropped.push({ item: a, message: 'Aktivitetstypen saknar koppling' }); continue; }
      input.push({
        externalId: a.externalId, startedAt: a.startedAt, durationSeconds: a.durationSeconds, activityTypeId: tm.activityTypeId,
        distanceMeters: a.distanceMeters ?? null, steps: a.steps ?? null, estimatedKcal: a.kcal ?? null, notes: a.name ?? null,
      });
    }
    const r = input.length ? (await api.importActivities(input, token)).importActivities : { created: 0, skipped: 0, failed: [] };
    const out = toRunnerResult(r, batch);
    out.failed.push(...dropped);
    return out;
  },

  async weights(batch, { api, token }) {
    const input = batch.map(w => ({
      externalId: w.externalId, date: w.date, weightKg: w.weightKg,
      fatPercent: w.fatPercent ?? null, musclePercent: w.musclePercent ?? null, waterPercent: w.waterPercent ?? null,
    }));
    return toRunnerResult((await api.importBodyMeasurements(input, token)).importBodyMeasurements, batch);
  },

  async measurements(batch, { api, token }) {
    // Bara kroppsmått: servern fyller på dagens viktrad (kräver att vikten importerats).
    const input = batch.map(m => ({ externalId: m.externalId, date: m.date, weightKg: m.weightKg ?? null, ...m.values }));
    return toRunnerResult((await api.importBodyMeasurements(input, token)).importBodyMeasurements, batch);
  },

  async steps(batch, { api, token }) {
    const input = batch.map(s => ({ date: s.date, steps: s.steps }));
    return toRunnerResult((await api.importDailySteps(input, token)).importDailySteps, batch);
  },

  async height(batch, { api, token }) {
    // Senaste värdet → profilen (bara ett i batchen är valt normalt).
    const latest = [...batch].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    await api.updateProfile({ heightCm: latest.heightCm }, token);
    return { done: batch.length, failed: [] };
  },
};

/*
 * Förberedelser före passen (live): skapa övningstyper för mappningar med
 * action "create" och bygg csvName → exerciseTypeId. Fel per övning
 * rapporteras; passets övriga övningar importeras ändå.
 */
async function prepareSessions(bundle, { api, token, onProgress }) {
  const ds = bundle.datasets.sessions;
  const errors = [];
  const typeMap = new Map();
  const creates = ds.mappings.filter(m => m.action === 'create' && !m.exerciseTypeId);
  let i = 0;
  for (const m of creates) {
    onProgress({ datasetKey: 'sessions', done: 0, total: ds.items.filter(x => x.selected).length, label: `skapar övning ${++i}/${creates.length}: ${m.create.name}` });
    try {
      const tok = typeof token === 'function' ? await token() : token;
      const d = await api.createExerciseType({
        name: m.create.name.trim(), description: null, muscleIds: m.create.muscleIds || [],
        tracksWeight: m.create.tracksWeight, tracksReps: m.create.tracksReps, tracksDistance: m.create.tracksDistance,
        tracksDuration: m.create.tracksDuration, isBodyweight: m.create.isBodyweight,
      }, tok);
      m.exerciseTypeId = d.createExerciseType?.id ?? null;
    } catch (e) {
      errors.push({ datasetKey: 'sessions', label: `Ny övning "${m.create.name}"`, message: e?.message || String(e) });
    }
  }
  for (const m of ds.mappings) {
    if ((m.action === 'map' || m.action === 'create') && m.exerciseTypeId) typeMap.set(m.csvName, m.exerciseTypeId);
  }
  ds.typeMap = typeMap;
  return errors;
}

async function saveAliases(bundle, { api, token }) {
  const input = bundle.datasets.sessions.mappings
    .filter(m => m.exerciseTypeId && m.action !== 'ignore')
    .map(m => ({ alias: m.csvName, exerciseTypeId: m.exerciseTypeId }));
  if (!input.length) return;
  const tok = typeof token === 'function' ? await token() : token;
  await api.setExerciseAliases(input, tok);
}
